import React, { useState } from 'react';
import {
  Settings,
  Cloud,
  HardDrive,
  Sun,
  Moon,
  Monitor,
  Calendar,
  Target,
  Flame,
  Award,
  AlertTriangle,
  Sparkles,
  CheckCircle2,
  Circle,
  MinusCircle,
  BarChart3,
  ChevronDown,
} from 'lucide-react';
import { useTheme } from '../theme/ThemeProvider';
import { WakeBall } from './AiSprite';

const THEME_META = {
  system: { Icon: Monitor, label: 'Theme: follows system (click for light)' },
  light: { Icon: Sun, label: 'Theme: light (click for dark)' },
  dark: { Icon: Moon, label: 'Theme: dark (click for system)' },
};

const STATS_KEY = 'xz-header-stats';

const Chip = ({ icon: Icon, value, label, tone, title }) => (
  <span
    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-700/60 border border-gray-600 text-xs whitespace-nowrap"
    title={title || label}
    aria-label={`${label}: ${value}`}
  >
    <Icon className={`w-3.5 h-3.5 ${tone}`} />
    <span className={`font-bold tabular-nums ${tone}`}>{value}</span>
    <span className="hidden sm:inline text-gray-400">{label}</span>
  </span>
);

/**
 * App header: brand, challenge stats + "Mark today", storage indicator, theme
 * cycle and settings. On wide screens (xl+) the stats sit inline on the brand
 * row so the header stays one line tall; below that they hide behind a toggle
 * next to the storage icon and expand as a second row on demand.
 * `stats` = { completed, total, streak, bestStreak, missedDays }
 * `todayStatus` = "none" | "done" | "partial" | null
 */
const Header = ({
  onSettingsClick,
  isCloud,
  stats,
  todayStatus,
  onMarkToday,
  aiName,
  aiAwake,
  onAiToggle,
}) => {
  const { theme, cycleTheme } = useTheme();
  const { Icon: ThemeIcon, label: themeLabel } = THEME_META[theme] || THEME_META.system;

  const [statsOpen, setStatsOpen] = useState(() => {
    try {
      return localStorage.getItem(STATS_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const toggleStats = () =>
    setStatsOpen((v) => {
      try {
        localStorage.setItem(STATS_KEY, v ? '0' : '1');
      } catch (e) {
        /* ignore */
      }
      return !v;
    });

  const s = stats || null;
  const remaining = s ? Math.max(0, (s.total || 0) - (s.completed || 0)) : 0;
  const TodayIcon =
    todayStatus === 'done' ? CheckCircle2 : todayStatus === 'partial' ? MinusCircle : Circle;
  const todayLabel =
    todayStatus === 'done' ? 'Today done' : todayStatus === 'partial' ? 'Today partial' : 'Mark today';
  const todayTone =
    todayStatus === 'done'
      ? 'bg-green-500/20 border-green-500/50 text-green-300'
      : todayStatus === 'partial'
        ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300'
        : 'bg-cyan-500/15 border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/25';

  const chips = s ? (
    <>
      {todayStatus !== null && todayStatus !== undefined && (
        <button
          type="button"
          onClick={onMarkToday}
          className={`tap flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap ${todayTone}`}
          title="Click to cycle: not done → done → partial"
        >
          <TodayIcon className="w-3.5 h-3.5" />
          {todayLabel}
        </button>
      )}
      <Chip icon={Calendar} value={s.completed} label="done" tone="text-yellow-400" title="Days completed" />
      <Chip icon={Target} value={remaining} label="left" tone="text-cyan-400" title="Days remaining" />
      <Chip icon={Flame} value={s.streak} label="streak" tone="text-orange-400" title="Current streak" />
      <Chip icon={Award} value={s.bestStreak} label="best" tone="text-purple-400" title="Best streak" />
      <Chip
        icon={s.missedDays > 0 ? AlertTriangle : Sparkles}
        value={s.missedDays}
        label="missed"
        tone={s.missedDays > 0 ? 'text-red-400' : 'text-green-400'}
        title="Missed days"
      />
    </>
  ) : null;

  const iconBtn =
    'tap bg-gray-700 hover:bg-gray-600 text-cyan-300 p-2 sm:p-3 rounded-xl sm:rounded-2xl transition-all duration-300 border-2 border-cyan-500/30 hover:border-cyan-500 hover:shadow-lg hover:shadow-cyan-500/20';

  return (
    <header className="glass border-x-0 border-t-0 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink-0">
            <div className="brand-gradient p-2 sm:p-3 rounded-xl sm:rounded-2xl shadow-lg flex-shrink-0">
              <img src="/assets/images/x-day-logo.png" alt="Xunzhine Logo" className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-xl sm:text-3xl md:text-4xl font-semibold text-transparent bg-clip-text brand-gradient truncate">
                Xunzhine
              </h1>
              <p className="text-cyan-300 text-xs sm:text-sm mt-0.5 hidden sm:block">
                Let's Get Started to Achieve your Dreams.
              </p>
            </div>
          </div>

          {/* Inline stats on wide screens — same row as the brand */}
          {chips && (
            <div
              className="hidden xl:flex flex-1 min-w-0 items-center justify-center gap-2 px-2 flex-wrap"
              aria-label="Challenge stats"
            >
              {chips}
            </div>
          )}

          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Stats toggle — only below the inline breakpoint */}
            {chips && (
              <button
                type="button"
                onClick={toggleStats}
                aria-expanded={statsOpen}
                aria-controls="header-stats"
                className={`${iconBtn} xl:hidden relative`}
                aria-label={statsOpen ? 'Hide challenge stats' : 'Show challenge stats'}
                title={statsOpen ? 'Hide challenge stats' : 'Show challenge stats'}
              >
                <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6" />
                <ChevronDown
                  className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 transition-transform ${
                    statsOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
            )}

            {/* AI companion wake/sleep ball (cloud accounts only) */}
            {isCloud && onAiToggle && (
              <button
                type="button"
                onClick={onAiToggle}
                aria-pressed={!!aiAwake}
                className={`${iconBtn} ${aiAwake ? 'border-purple-400/60 bg-purple-500/15' : ''}`}
                aria-label={aiAwake ? `Put ${aiName || 'the AI'} to sleep` : `Wake ${aiName || 'the AI'}`}
                title={aiAwake ? `Put ${aiName || 'the AI'} to sleep` : `Wake ${aiName || 'the AI'}`}
              >
                <WakeBall size={22} open={!!aiAwake} className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            )}

            {/* Storage mode indicator: colored cloud (cloud sync) or local disk */}
            {isCloud ? (
              <div
                className="p-2 sm:p-3 rounded-xl sm:rounded-2xl border-2 border-cyan-500/50 bg-cyan-500/15 text-cyan-300"
                title="Cloud storage — your data syncs across devices"
                aria-label="Cloud storage"
              >
                <Cloud className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            ) : (
              <div
                className="p-2 sm:p-3 rounded-xl sm:rounded-2xl border-2 border-amber-500/50 bg-amber-500/15 text-amber-300"
                title="Local storage — your data is saved on this device"
                aria-label="Local storage"
              >
                <HardDrive className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            )}

            {/* Theme cycle: system → light → dark */}
            <button onClick={cycleTheme} className={iconBtn} aria-label={themeLabel} title={themeLabel}>
              <ThemeIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <button onClick={onSettingsClick} className={iconBtn} aria-label="Settings" title="Settings">
              <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Collapsible stats row — below xl, only when toggled open */}
        {chips && statsOpen && (
          <div
            id="header-stats"
            className="xl:hidden mt-3 flex flex-wrap items-center gap-2"
            aria-label="Challenge stats"
          >
            {chips}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
